import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  ReloadOutlined,
  StopOutlined,
} from "@ant-design/icons";
import {
  Button,
  Flex,
  List,
  Modal,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import type { TableProps } from "antd/lib/index";
import { format } from "date-fns";
import { groupBy, values } from "lodash";
import pluralize from "pluralize";
import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { color, font, radius } from "theme/aiInventory";

import { RequestStatusTag } from "../../../../Jit/Requests/components/RequestStatusTag.tsx";
import { EmptyState } from "../EmptyState.tsx";
import type { SessionSummary } from "../graph/types.ts";
import { useEnabledServersForClient } from "../hooks/useEnabledServersForClient.ts";
import type { McpCallRecord } from "../hooks/useMcpAccessLogs.ts";
import type { McpClient } from "../hooks/useMcpClients.ts";
import { useScopedMcpCallRecords } from "../hooks/useScopedMcpCallRecords.ts";
import { UpdatePolicyModal } from "./UpdatePolicyModal.tsx";

const PRE_ACCESS_TOOLS = new Set<string>([
  "access",
  "check",
  "list",
  "relinquish",
]);

const kindLabel = (kind: SessionSummary["kind"]): string =>
  kind === "tool-call" ? "TOOL CALL" : "REQUEST";

const serverNameFor = (record: McpCallRecord): string =>
  record.request.event?.["mcp-server"]?.name ??
  record.request.event?.["mcp-server"]?.id ??
  record.request.event?.["mcp-server"]?.path ??
  "-";

type SessionRow = {
  readonly key: string;
  readonly sessionId: string;
  readonly serverName: string;
  readonly summary: SessionSummary;
};

const UpdatePolicy: React.FC<{
  action: "allow" | "block";
  summary: SessionSummary;
  onUpdate: (requestId: string, action: "allow" | "block") => void;
}> = ({ action, onUpdate, summary }) => {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (summary.requestId) {
        onUpdate(summary.requestId, action);
      }
    },
    [action, onUpdate, summary]
  );
  return (
    summary.requestId && (
      <Button
        icon={action === "allow" ? <CheckCircleOutlined /> : <StopOutlined />}
        style={action === "allow" ? { color: "rgb(22, 119, 255)" } : undefined}
        type="text"
        danger={action === "block"}
        onClick={handleClick}
      >
        {action}
      </Button>
    )
  );
};

const isAllowable = (summary: SessionSummary) =>
  summary.kind === "tool-call" &&
  summary.toolName === "check" &&
  summary.requestStatus === "DENIED";

const isBlockable = (summary: SessionSummary) =>
  summary.kind === "tool-call" && !PRE_ACCESS_TOOLS.has(summary.toolName);

const rowStyle = { cursor: "pointer" } as const;

export type ClientSidecarProps = {
  client: McpClient;
  onClose: () => void;
  onBack?: () => void;
};

export const ClientSidecar: React.FC<ClientSidecarProps> = ({
  client,
  onClose,
  onBack,
}) => {
  const { records, loading, refetch } = useScopedMcpCallRecords({
    client,
    maxRecords: 50,
  });
  const { servers: enabledServers } = useEnabledServersForClient(client);
  const [openRecord, setOpenRecord] = useState<McpCallRecord | undefined>();
  const handleCloseRecord = useCallback(() => setOpenRecord(undefined), []);

  const [currentPolicy, setCurrentPolicy] = useState<{
    requestId: string;
    action: "allow" | "block";
  }>();
  const handleUpdateAction = useCallback(
    (requestId: string, action: "allow" | "block") => {
      setCurrentPolicy({ requestId, action });
    },
    []
  );
  const handleActionDone = useCallback(() => setCurrentPolicy(undefined), []);

  const openSummary = useCallback(
    (summary: SessionSummary) => {
      setOpenRecord(records.find((r) => r.summary.id === summary.id));
    },
    [records]
  );

  const sessionRows = useMemo<SessionRow[][]>(
    () =>
      values(
        groupBy(
          records
            .filter((r) => r.summary.kind === "tool-call")
            .map((record) => ({
              key: record.summary.id,
              requestId: record.summary.requestId,
              sessionId: record.sessionId,
              serverName: serverNameFor(record),
              summary: record.summary,
            })),
          "sessionId"
        )
      ),
    [records]
  );

  const sessionColumns = useMemo(
    () => [
      {
        title: "Time",
        dataIndex: "summary",
        key: "time",
        width: 130,
        render: (summary: SessionSummary) => (
          <Typography.Text
            type="secondary"
            style={{ fontFamily: font.mono, fontSize: 11 }}
            title={summary.eventAt}
          >
            {format(new Date(summary.eventAt), "MMM d, HH:mm:ss")}
          </Typography.Text>
        ),
      },
      {
        title: "Tool",
        dataIndex: "summary",
        key: "activity",
        width: 120,
        render: (summary: SessionSummary) => (
          <Typography.Text>{summary.toolName}</Typography.Text>
        ),
      },
      {
        title: "Server",
        dataIndex: "serverName",
        key: "server",
        width: 120,
        render: (serverName: string) => (
          <Typography.Text
            style={{ fontFamily: font.mono, fontSize: 11 }}
            ellipsis
          >
            {serverName}
          </Typography.Text>
        ),
      },
      {
        title: "Request",
        dataIndex: "summary",
        key: "session",
        width: 130,
        render: ({ requestId }: SessionRow["summary"]) =>
          requestId && (
            <Link
              style={{ fontFamily: font.mono, fontSize: 11 }}
              to={`../access-management/history/${requestId}`}
            >
              {requestId.slice(0, 16)}
            </Link>
          ),
      },
      {
        title: "Status",
        dataIndex: "summary",
        key: "session",
        width: 140,
        render: ({ requestStatus }: SessionRow["summary"]) =>
          requestStatus && <RequestStatusTag status={requestStatus} />,
      },
      {
        title: "Session",
        dataIndex: "sessionId",
        key: "session",
        width: 110,
        render: (sessionId: string) => (
          <Typography.Text
            type="secondary"
            style={{ fontFamily: font.mono, fontSize: 11 }}
          >
            {sessionId.slice(0, 8)}
          </Typography.Text>
        ),
      },
      {
        title: "Actions",
        dataIndex: "summary",
        key: "action",
        width: 120,
        render: (summary: SessionSummary) => (
          <Flex>
            {isAllowable(summary) && (
              <UpdatePolicy
                action="allow"
                onUpdate={handleUpdateAction}
                summary={summary}
              />
            )}
            {isBlockable(summary) && (
              <UpdatePolicy
                action="block"
                onUpdate={handleUpdateAction}
                summary={summary}
              />
            )}
          </Flex>
        ),
      },
    ],
    [handleUpdateAction]
  );

  const handleRow = useCallback<NonNullable<TableProps<SessionRow>["onRow"]>>(
    (row) => ({
      onClick: () => openSummary(row.summary),
      style: rowStyle,
    }),
    [openSummary]
  );

  const toolCallCount = useMemo(
    () => records.filter((r) => r.summary.kind === "tool-call").length,
    [records]
  );

  const requestCount = useMemo(
    () =>
      records.filter(
        (r) => r.summary.kind === "tool-call" && r.summary.toolName === "access"
      ).length,
    [records]
  );

  const createdLabel = client.createdAt
    ? format(client.createdAt, "PPpp")
    : "-";

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Space style={{ justifyContent: "space-between", width: "100%" }}>
        {onBack ? (
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack}>
            Back
          </Button>
        ) : (
          <span />
        )}
        <Button type="text" icon={<CloseOutlined />} onClick={onClose} />
      </Space>
      <Space size={8} align="center" wrap>
        <Typography.Title level={4} style={{ margin: 0 }}>
          {client.id}
        </Typography.Title>
        <Tag color="blue">MCP Client</Tag>
        {client.platform && <Tag>{client.platform}</Tag>}
      </Space>
      <div
        style={{
          border: `1px solid ${color.rule}`,
          borderRadius: radius.card,
          padding: 12,
          fontFamily: font.mono,
          fontSize: 11,
          color: color.inkMute,
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          gap: "4px 12px",
        }}
      >
        <span>platform</span>
        <span style={{ color: color.ink }}>{client.platform}</span>
        <span>version</span>
        <span style={{ color: color.ink }}>{client.version}</span>
        <span>redirect</span>
        <span style={{ color: color.ink }}>{client.redirectUri}</span>
        {client.createdBy?.user && (
          <>
            <span>registered by</span>
            <span style={{ color: color.ink }}>{client.createdBy.user}</span>
          </>
        )}
        <span>registered</span>
        <span style={{ color: color.ink }}>{createdLabel}</span>
      </div>
      {enabledServers.length > 0 && (
        <div>
          <Space size={6} align="center" style={{ marginBottom: 6 }}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              Enabled servers
            </Typography.Title>
            <Typography.Text
              type="secondary"
              style={{ fontFamily: font.mono, fontSize: 11 }}
            >
              {enabledServers.length} via gateway
            </Typography.Text>
          </Space>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {enabledServers.map((server) => (
              <Tag key={server.id} style={{ fontFamily: font.mono, margin: 0 }}>
                {server.label}
              </Tag>
            ))}
          </div>
        </div>
      )}
      <div>
        <Space
          size={8}
          align="center"
          style={{
            marginBottom: 8,
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Space size={8} align="center">
            <Typography.Title level={5} style={{ margin: 0 }}>
              Recent sessions
            </Typography.Title>
            {sessionRows.length > 0 && (
              <Typography.Text
                type="secondary"
                style={{ fontFamily: font.mono, fontSize: 11 }}
              >
                {sessionRows.length} {pluralize("session", sessionRows.length)}{" "}
                · {toolCallCount} tool {pluralize("call", toolCallCount)} ·{" "}
                {requestCount} access {pluralize("request", requestCount)}
              </Typography.Text>
            )}
          </Space>
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined spin={loading} />}
            onClick={refetch}
            disabled={loading}
            aria-label="Refresh sessions"
          />
        </Space>
        {loading && sessionRows.length === 0 ? (
          <div style={{ padding: 16, textAlign: "center" }}>
            <Spin size="small" />
          </div>
        ) : sessionRows.length === 0 ? (
          <EmptyState
            title="No recent sessions"
            hint="Traffic from this client will appear here once it's active."
          />
        ) : (
          <List>
            {sessionRows.map((row, ix) => (
              <List.Item key={ix}>
                <Table
                  size="small"
                  columns={sessionColumns}
                  dataSource={row}
                  pagination={false}
                  onRow={handleRow}
                />
              </List.Item>
            ))}
          </List>
        )}
      </div>
      {currentPolicy && (
        <UpdatePolicyModal
          action={currentPolicy?.action}
          agent={client.id}
          onClose={handleActionDone}
          open
          requestId={currentPolicy?.requestId}
        />
      )}
      <Modal
        open={!!openRecord}
        onCancel={handleCloseRecord}
        title={
          openRecord
            ? `${kindLabel(openRecord.summary.kind)} · ${openRecord.summary.toolName}`
            : undefined
        }
        footer={null}
        width={720}
      >
        {openRecord && (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <Typography.Text type="secondary" style={{ fontFamily: font.mono }}>
              session {openRecord.sessionId} · message{" "}
              {String(openRecord.request.event?.message?.id)} ·{" "}
              {openRecord.summary.durationMs} ms
            </Typography.Text>
            <Typography.Title level={5} style={{ margin: 0 }}>
              Request
            </Typography.Title>
            <pre
              style={{
                background: color.mute,
                padding: 14,
                borderRadius: radius.card,
                overflow: "auto",
                fontSize: 12,
                fontFamily: font.mono,
                lineHeight: 1.5,
                margin: 0,
                maxHeight: 240,
              }}
            >
              {JSON.stringify(openRecord.request, null, 2)}
            </pre>
            <Typography.Title level={5} style={{ margin: 0 }}>
              Response
            </Typography.Title>
            <pre
              style={{
                background: color.mute,
                padding: 14,
                borderRadius: radius.card,
                overflow: "auto",
                fontSize: 12,
                fontFamily: font.mono,
                lineHeight: 1.5,
                margin: 0,
                maxHeight: 240,
              }}
            >
              {JSON.stringify(openRecord.response, null, 2)}
            </pre>
          </Space>
        )}
      </Modal>
    </Space>
  );
};
